import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import type { components as visionComponents } from '@/api/types.vision.gen';

// Модерация рекламы Starling Vision (V1.4) — вкладка «Реклама» раздела
// «Модерация». Как и жалобы, ходит в продуктовый неймспейс
// /api/v1/vision/admin/ads* под платформенным гейтом (VD10); типы — из
// генерата контракта Vision (`types.vision.gen.ts`).
//
// Жизненный цикл заявки: pending → approved (ждёт оплаты) → active (оплачено,
// показ) → expired; из pending/approved — rejected (с причиной) или
// withdrawn (заявитель сам); active ⇄ paused (пауза/возобновление, оплаченный
// срок не сгорает — `ends_at` сдвигается на длительность паузы); из active и
// paused — stopped (снял админ, окончательно). Все переходы на бэке атомарны:
// 409 `ad_invalid_transition` — статус уже сменился.

export type AdminAd = visionComponents['schemas']['AdminAd'];
export type AdminAdPage = visionComponents['schemas']['AdminAdPage'];
export type AdStatus = visionComponents['schemas']['AdStatus'];
export type AdRejectRequest = visionComponents['schemas']['AdRejectRequest'];
export type AdActivateRequest = visionComponents['schemas']['AdActivateRequest'];

/**
 * Сегменты вкладки: очередь / ждут оплаты / идёт показ (`active` + `paused` —
 * ими управляют в одной вкладке) / архив (rejected|withdrawn|expired|stopped).
 */
export type AdminAdsState = 'pending' | 'approved' | 'active' | 'archive';
export const ADMIN_ADS_STATES: readonly AdminAdsState[] = [
  'pending',
  'approved',
  'active',
  'archive',
];

export const ADMIN_ADS_KEY = ['vision', 'admin', 'ads'] as const;
export const ADMIN_ADS_PAGE_SIZE = 20;

export interface AdminAdsParams {
  state: AdminAdsState;
  page: number;
}

export function useAdminAdsQuery(params: AdminAdsParams) {
  return useQuery({
    queryKey: [...ADMIN_ADS_KEY, params.state, params.page],
    queryFn: () =>
      api.get<AdminAdPage>(
        `/api/v1/vision/admin/ads?state=${params.state}&page=${params.page}&page_size=${ADMIN_ADS_PAGE_SIZE}`,
      ),
    placeholderData: (prev) => prev,
  });
}

/** Общий хвост мутаций: любой переход меняет состав нескольких сегментов — инвалидируем все. */
function useInvalidateAds() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ADMIN_ADS_KEY });
  };
}

/** Одобрить (pending → approved): заявителю уходит письмо с суммой и реквизитами. */
export function useApproveAdMutation() {
  const invalidate = useInvalidateAds();
  return useMutation({
    mutationKey: [...ADMIN_ADS_KEY, 'approve'],
    mutationFn: ({ adId }: { adId: string }) =>
      api.post<AdminAd>(`/api/v1/vision/admin/ads/${adId}/approve`),
    onSettled: invalidate,
  });
}

/** Отклонить с причиной (pending|approved → rejected). */
export function useRejectAdMutation() {
  const invalidate = useInvalidateAds();
  return useMutation({
    mutationKey: [...ADMIN_ADS_KEY, 'reject'],
    mutationFn: ({ adId, reason }: { adId: string; reason: string }) =>
      api.post<AdminAd>(`/api/v1/vision/admin/ads/${adId}/reject`, {
        reason,
      } satisfies AdRejectRequest),
    onSettled: invalidate,
  });
}

/**
 * «Оплачено» → запуск показа (approved → active). `starts_at` — начало показа;
 * без него бэкенд берёт `now` (тело без поля, не null — см. контракт).
 */
export function useActivateAdMutation() {
  const invalidate = useInvalidateAds();
  return useMutation({
    mutationKey: [...ADMIN_ADS_KEY, 'activate'],
    mutationFn: ({ adId, startsAt }: { adId: string; startsAt?: string }) =>
      api.post<AdminAd>(
        `/api/v1/vision/admin/ads/${adId}/activate`,
        (startsAt ? { starts_at: startsAt } : {}) satisfies AdActivateRequest,
      ),
    onSettled: invalidate,
  });
}

/** Снять с показа окончательно (active | paused → stopped). */
export function useStopAdMutation() {
  const invalidate = useInvalidateAds();
  return useMutation({
    mutationKey: [...ADMIN_ADS_KEY, 'stop'],
    mutationFn: ({ adId }: { adId: string }) =>
      api.post<AdminAd>(`/api/v1/vision/admin/ads/${adId}/stop`),
    onSettled: invalidate,
  });
}

/** Приостановить показ (active → paused): баннер уходит из ленты, `paused_at = now`. */
export function usePauseAdMutation() {
  const invalidate = useInvalidateAds();
  return useMutation({
    mutationKey: [...ADMIN_ADS_KEY, 'pause'],
    mutationFn: ({ adId }: { adId: string }) =>
      api.post<AdminAd>(`/api/v1/vision/admin/ads/${adId}/pause`),
    onSettled: invalidate,
  });
}

/** Возобновить показ (paused → active): `ends_at` сдвигается на длительность паузы. */
export function useResumeAdMutation() {
  const invalidate = useInvalidateAds();
  return useMutation({
    mutationKey: [...ADMIN_ADS_KEY, 'resume'],
    mutationFn: ({ adId }: { adId: string }) =>
      api.post<AdminAd>(`/api/v1/vision/admin/ads/${adId}/resume`),
    onSettled: invalidate,
  });
}
